import React, { useEffect, useMemo, useState } from "react";
import { Spin, Table } from "antd";
import { ColumnType } from "antd/lib/table";
import {
  AiccCanDoStatement,
  AiccUserCanDoStatements,
  AiccUserVocabulary,
  AiccVocabularyWord,
} from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { canDoColumns, getVocabularyColumns } from "./columnConfig";
import styles from "./LearningAssets.module.scss";

interface Props {
  subUserId: string;
  languageId: number;
}

const TABLE_BODY_HEIGHT = 400;
const EMPTY_TABLE_BODY_HEIGHT = 100;

interface SectionProps<T> {
  title: string;
  columns: ColumnType<T>[];
  dataSource: T[];
  rowKey: (record: T) => string;
}

function AssetSection<T extends object>({ title, columns, dataSource, rowKey }: SectionProps<T>) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      <Table<T>
        className={styles.table}
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        scroll={{ y: dataSource.length ? TABLE_BODY_HEIGHT : EMPTY_TABLE_BODY_HEIGHT }}
        pagination={false}
      />
    </div>
  );
}

const vocabularyRowKey = (record: AiccVocabularyWord) => record.word;

const canDoRowKey = (record: AiccCanDoStatement) => record.id;

function LearningAssets({ subUserId, languageId }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [vocabulary, setVocabulary] = useState<AiccUserVocabulary>();
  const [canDoStatements, setCanDoStatements] = useState<AiccUserCanDoStatements>();

  useEffect(() => {
    setIsLoading(true);

    const loadVocabulary = aiccAPI
      .fetchAiccUserVocabulary(subUserId, languageId)
      .then(setVocabulary)
      .catch(() => {
        setVocabulary(undefined);
      });

    const loadCanDoStatements = aiccAPI
      .fetchAiccUserCanDoStatements(subUserId, languageId)
      .then(setCanDoStatements)
      .catch(() => {
        setCanDoStatements(undefined);
      });

    Promise.all([loadVocabulary, loadCanDoStatements]).finally(() => setIsLoading(false));
  }, [subUserId, languageId]);

  const vocabularyColumns = useMemo(() => getVocabularyColumns(vocabulary?.hasTransliteration ?? false), [
    vocabulary?.hasTransliteration,
  ]);

  return (
    <Spin spinning={isLoading}>
      <AssetSection<AiccVocabularyWord>
        title="Known Vocabulary"
        columns={vocabularyColumns}
        dataSource={vocabulary?.known ?? []}
        rowKey={vocabularyRowKey}
      />
      <AssetSection<AiccVocabularyWord>
        title="New Vocabulary"
        columns={vocabularyColumns}
        dataSource={vocabulary?.new ?? []}
        rowKey={vocabularyRowKey}
      />
      <AssetSection<AiccCanDoStatement>
        title="Can-Do Statements"
        columns={canDoColumns}
        dataSource={canDoStatements?.items ?? []}
        rowKey={canDoRowKey}
      />
    </Spin>
  );
}

export default LearningAssets;
